<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.user_notification') }}
      <template v-slot:right>
        <b-button @click="goToNotificationCreate" size="sm" class="ml-1" variant="success">{{ $t('btn.add_new_notification') }}</b-button>
      </template>
    </page-header>

    <page-card>
      <template v-slot:header>
        {{ $t('page.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid sygni-grid--standard-width-datepicker">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="id"
                  :label="$t('filters.label.notification.id')"
                  :placeholder="$t('filters.placeholder.message.id')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="name"
                  :label="$t('filters.label.notification.name')"
                  :placeholder="$t('filters.placeholder.notification.name')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="createdAt"
                  open-direction="left"
                  :label="$t('filters.label.notification.created_at')">
              </filter-date-range-picker>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="selectOptions.status"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="status"
                  :label="$t('filters.label.notification.status')"
                  :placeholder="$t('filters.placeholder.notification.status')">>
              </filter-select>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="displayDateFrom"
                  open-direction="left"
                  :label="$t('filters.label.notification.display_date_from')">
              </filter-date-range-picker>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="displayDateTo"
                  open-direction="left"
                  :label="$t('filters.label.notification.display_date_to')">
              </filter-date-range-picker>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{ $t('filters.btn.clear') }}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{ $t('filters.btn.apply') }}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.list')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
          <div class="table-responsive">
            <b-table striped
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     show-empty
                     :empty-text="$t('table.no_results')"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template #cell(id)="data">
                {{ data.item.id | shortId }}
              </template>

              <template #cell(displayDateFrom)="data">
                {{ data.item.displayDateFrom | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(displayDateTo)="data">
                {{ data.item.displayDateTo | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(createdAt)="data">
                {{ data.item.createdAt | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(segments)="data">
                <div v-for="segment in data.value" :key="segment.id">
                  {{ segment.name }}
                </div>
              </template>

              <template #cell(isApprovalRequired)="data">
                <b-badge pill variant="success" v-if="data.item.isApprovalRequired">{{$t('notification_is_approval_required') }}</b-badge>
                <b-badge pill variant="danger" v-if="!data.item.isApprovalRequired">{{ $t('notification_is_not_approval_required') }}</b-badge>
              </template>

              <template #cell(status)="data">
                <b-badge pill variant="primary" v-if="data.item.status === 'draft'">{{$t('notification_status_'+ data.value )}}</b-badge>
                <b-badge pill variant="success" v-if="data.item.status === 'active'">{{$t('notification_status_'+ data.value )}}</b-badge>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <b-button variant="primary" size="sm" @click="openModal('show', data.item)">{{ $t('table.btn.notification.show_details') }}</b-button>
                  <b-button variant="success" size="sm" @click="goToNotificationCreate('edit', data.item)">{{ $t('table.btn.notification.edit') }}</b-button>
                  <b-button variant="warning" size="sm" @click="goToNotificationCreate('', data.item)">{{ $t('table.btn.notification.duplicate') }}</b-button>
                  <b-button variant="danger" size="sm" @click="deleteNotification(data.item)">{{ $t('table.btn.notification.delete') }}</b-button>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
        </div>
      </template>
      <template v-slot:footer>

      </template>
    </page-card>

  </div>
</template>

<script>
import GridMixin from "../../../components/grid/GridMixin";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";
import ShowNotificationModal from "./ShowNotificationModal";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import alertify from "alertifyjs";

export default {
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    GridPagination,
    FilterInput,
    FilterSelect,
    FilterDateRangePicker
  },
  data() {
    return {
      gridGetUrl: '/api/inbox/notifications',
      defaultFilters: '',
      fields: [
        {key: 'id', label: this.$t('table.header.notification.id'), sortable: false},
        {key: 'name', label: this.$t('table.header.notification.name'), sortable: true},
        {key: 'createdAt', label: this.$t('table.header.notification.created_at'), sortable: true},
        {key: 'isApprovalRequired', label: this.$t('table.header.notification.is_approval_required'), sortable: true},
        {key: 'segments', label: this.$t('table.header.notification.segments'), sortable: true},
        {key: 'status', label: this.$t('table.header.notification.notification_status'), sortable: true},
        {key: 'displayDateFrom', label: this.$t('table.header.notification.display_date_from'), sortable: true},
        {key: 'displayDateTo', label: this.$t('table.header.notification.display_date_to'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions'), sortable: false}
      ],
      selectOptions: {
        status: [
          {value: 'draft', text: this.$t('option.notification.draft')},
          {value: 'active', text: this.$t('option.notification.active')},
        ]
      },
      perPage: 10,
      currentPage: 1,
      orderColumn: 'createdAt',
      orderDirection: false,
      createModalInstance: null
    };
  },
  mounted() {
    this.$on('refreshGrid', () => {
      this.applyFilters();
    })
  },

  methods: {
    openModal(type, data) {
      switch (type) {
        case('show'): {
          this.$store.commit('modal/openModal', {parent: this, modal: ShowNotificationModal, modalName: 'showModal', data});
          break;
        }
        // case('edit'): {
        //   this.$store.commit('modal/openModal', {
        //     parent: this,
        //     modal: EditQuestionModal,
        //     modalName: 'createModal',
        //     data,
        //     props: {apiKey: this.apiKey, selectOptions: this.selectOptions}
        //   });
        //   break;
        // }
      }
    },
    deleteNotification(notification) {
      this.$bvModal.msgBoxConfirm(`${this.$t('modal.title.remove_notification_name')}: ${notification.name}`, {
        title: this.$t('modal.title.confirm_remove_notification'),
        okTitle: this.$t('btn.confirm'),
        cancelTitle: this.$t('btn.cancel'),
        okVariant: 'success',
      })
      .then(value => {
        if (value) {
          this.$store.dispatch('userNotification/deleteNotification', notification.id).then(() => {
            this.loadItems();
            alertify.success(this.$t('notify.resale.confirmation.success'));
          })
          .catch(() => {
            alertify.error(this.$t('notify.resale.confirmation.error'));
          });
        }
      });
    },
    goToNotificationCreate(actionType, notification) {
      const params = {
        actionType: actionType,
        ...notification
      }

      this.$router.push({ name: `user-notification-create`, params: params });
    }
  }
}
</script>
